import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormHelperText,
  Collapse,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { emailSignup } from "../../../apis/user-apis/UserBottomNavbarApis";

const NAME_REGEX = /^[\p{L}\p{M}\s'-’]+$/u;
const filterName = (value) => value.replace(/[^\p{L}\p{M}\s'-’]/gu, "");

const UserBottomNavbar = () => {
  const isLarge = useMediaQuery("(min-width:769px)");
  const isCompact = useMediaQuery("(max-width:1100px)");
  const [expanded, setExpanded] = useState(false);

  const [formData, setFormData] = useState({ name: "", email: "" });
  const [errors, setErrors] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false); // ← tracks success test

  const validate = () => {
    const tempErrors = { name: "", email: "" };
    let isValid = true;
    const nameTrimmed = formData.name.trim();
    if (!nameTrimmed) {
      tempErrors.name = "Name is required";
      isValid = false;
    } else if (nameTrimmed.split(/\s+/).filter(Boolean).length < 2) {
      tempErrors.name = "Please enter full name";
      isValid = false;
    } else if (!NAME_REGEX.test(nameTrimmed)) {
      tempErrors.name = "Invalid name";
      isValid = false;
    }
    if (!formData.email.trim()) {
      tempErrors.email = "Email is required";
      isValid = false;
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      tempErrors.email = "Invalid email address";
      isValid = false;
    }
    setErrors(tempErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "name" ? filterName(value) : value;

    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      await emailSignup({
        name: formData.name,
        email: formData.email,
        source: "Email Bar",
      });
      setSubmitted(true); // ← triggers button text change
      setFormData({ name: "", email: "" });
      if (!isLarge) setExpanded(false);
    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Something went wrong. Try again.";

      setErrors((prev) => ({
        ...prev,
        email: errorMsg,
      }));
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    backgroundColor: "#FFF",
    borderRadius: "4px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": { border: "none" },
      borderRadius: "4px",
      height: isCompact && isLarge ? "36px" : "40px",
    },
    "& .MuiInputBase-input": {
      fontSize: isCompact && isLarge ? "13px" : "14px",
      fontFamily: "inherit",
      padding: isCompact && isLarge ? "0 12px" : "0 14px",
    },
    "& input:-webkit-autofill": {
      padding: isCompact && isLarge ? "8px" : "10px",
    },
  };

  return (
    <Box
      component="section"
      aria-label="Stay informed email signup"
      sx={{
        backgroundColor: "#C59324",
        position: "fixed",
        top: "52px",
        left: 0,
        right: 0,
        zIndex: 1099,
        padding: { xs: "8px 16px", sm: "8px 24px", md: "6px 24px", lg: "8px 48px" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: { xs: "12px", md: "12px", lg: "24px" },
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        overflow: "hidden",
      }}
    >
      {/* ── Desktop (lg and up) — inline form ────────────────── */}
      {isLarge && (
        <>
          <Typography
            sx={{
              fontSize: isCompact ? "13px" : "14px",
              fontWeight: 700,
              color: "#0A192F",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexShrink: 0,
            }}
          >
            <span role="img" aria-label="megaphone">
              📣
            </span>
            Stay informed: get campaign updates from Gary Taylor:
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: "flex", gap: isCompact ? "8px" : "12px", alignItems: "flex-start", ...(isCompact ? { flex: 1, minWidth: 0 } : {}) }}
          >
            {/* Name */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Full Name*"
                variant="outlined"
                size="small"
                fullWidth
                disabled={submitted}
                sx={fieldSx}
              />
              {errors.name && (
                <FormHelperText
                  sx={{
                    color: "#7a3a00",
                    ml: 1,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                >
                  {errors.name}
                </FormHelperText>
              )}
            </Box>

            {/* Email */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com*"
                variant="outlined"
                size="small"
                fullWidth
                disabled={submitted}
                sx={fieldSx}
              />
              {errors.email && (
                <FormHelperText
                  sx={{
                    color: "#7a3a00",
                    ml: 1,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                >
                  {errors.email}
                </FormHelperText>
              )}
            </Box>

            {/* Button — turns green "Signed Up! ✓" after success */}
            <Button
              type="submit"
              variant="contained"
              disabled={loading || submitted}
              sx={{
                backgroundColor: submitted ? "#2e7d32" : "#0A192F",
                color: "#FFF",
                fontWeight: 700,
                textTransform: "none",
                height: isCompact ? "36px" : "40px",
                padding: isCompact ? "0 18px" : "0 24px",
                borderRadius: "4px",
                fontSize: isCompact ? "13px" : "14px",
                minWidth: "unset",
                whiteSpace: "nowrap",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: submitted ? "#1b5e20" : "#142a4a",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: submitted ? "#2e7d32" : undefined,
                  color: submitted ? "#FFF" : undefined,
                },
              }}
            >
              {loading
                ? "Submitting..."
                : submitted
                  ? "Signed Up! ✓"
                  : "Stay Informed"}
            </Button>
          </Box>
        </>
      )}

      {/* ── Smaller screens — dropdown/collapse ──────────────── */}
      {!isLarge && (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              py: 0.5,
            }}
            onClick={() => setExpanded((o) => !o)}
          >
            <Typography
              sx={{
                color: "#0A192F",
                fontWeight: 700,
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span role="img" aria-label="megaphone">
                📣
              </span>
              {submitted ? "✓ You're signed up!" : "Get campaign updates"}
            </Typography>
            <IconButton size="small" sx={{ color: "#0A192F", p: 0 }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={expanded && !submitted}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                pt: 1.5,
                pb: 1,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <Box>
                <TextField
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Full Name*"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                />
                {errors.name && (
                  <FormHelperText
                    sx={{ color: "#7a3a00", ml: 1, fontWeight: 600 }}
                  >
                    {errors.name}
                  </FormHelperText>
                )}
              </Box>
              <Box>
                <TextField
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com*"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                />
                {errors.email && (
                  <FormHelperText
                    sx={{ color: "#7a3a00", ml: 1, fontWeight: 600 }}
                  >
                    {errors.email}
                  </FormHelperText>
                )}
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                fullWidth
                sx={{
                  backgroundColor: "#0A192F",
                  color: "#FFF",
                  fontWeight: 700,
                  textTransform: "none",
                  height: "45px",
                  borderRadius: "4px",
                  fontSize: "15px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#142a4a",
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? "Submitting..." : "Stay Informed"}
              </Button>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default UserBottomNavbar;
